/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React from 'react'
import webStyled, { CSSObject, DefaultTheme, useTheme } from 'styled-components'

import { ExternalSiteFilled } from 'ui/svg/icons/ExternalSiteFilled'

import { getIconSize } from './getIconSize'
import { getLinkGap } from './getLinkGap'
import { getLinkIconColor } from './getLinkIconColor'
import { getLinkTextColor } from './getLinkTextColor'
import { getLinkTypography } from './getLinkTypography'
import { LinkAnchor } from './LinkAnchor.web'
import { LinkProps, LinkSize } from './types'

type LinkAnchorComponentProps = React.ComponentProps<typeof LinkAnchor> & {
  style?: unknown
}

export const Link = ({
  label,
  wording,
  size = 'default',
  color = 'brand',
  icon,
  isExternal = false,
  isInsideText = false,
  textColor,
  testID,
  accessibilityLabel,
  ...props
}: LinkProps) => {
  const computedLabel = label ?? wording ?? ''
  const theme = useTheme()
  const Icon = icon ?? (isExternal ? ExternalSiteFilled : undefined)
  const iconSize = getIconSize({ size, theme })
  const iconColor = getLinkIconColor({ color, theme })
  const { href, style: _style, ...anchorProps } = props as unknown as LinkAnchorComponentProps

  if (isInsideText) {
    const InlineIcon = icon ?? (isExternal ? ExternalSiteFilled : undefined)

    return (
      <InlineTextContainer
        {...anchorProps}
        accessibilityLabel={
          accessibilityLabel ??
          getInsideTextAccessibilityLabel({
            accessibilityRole: anchorProps.accessibilityRole,
            isExternal,
            label: computedLabel,
          })
        }
        color={color}
        href={href ?? '#'}
        testID={testID}
        $textColor={textColor}
        $size={size}>
        {InlineIcon ? (
          <InlineIcon color={iconColor} size={iconSize} testID="link-icon" aria-hidden />
        ) : null}
        <InlineTextLabel>{computedLabel}</InlineTextLabel>
      </InlineTextContainer>
    )
  }

  return (
    <Container
      {...anchorProps}
      accessibilityLabel={
        accessibilityLabel ?? getAccessibilityLabel({ isExternal, label: computedLabel })
      }
      color={color}
      href={href ?? '#'}
      testID={testID}>
      <InlineContent size={size}>
        {Icon ? <Icon color={iconColor} size={iconSize} testID="link-icon" aria-hidden /> : null}
        <Text data-testid="link-label" size={size} color={color} textColor={textColor}>
          {computedLabel}
        </Text>
      </InlineContent>
    </Container>
  )
}

function getAccessibilityLabel({ isExternal, label }: { isExternal: boolean; label: string }) {
  return isExternal ? `Nouvelle fenêtre\u00a0: ${label}` : label
}

function getInsideTextAccessibilityLabel({
  accessibilityRole,
  isExternal,
  label,
}: {
  accessibilityRole?: LinkProps['accessibilityRole'] | string
  isExternal: boolean
  label: string
}) {
  return isExternal || accessibilityRole === 'link' ? `${label}, lien externe` : label
}

const getContainerStyle = ({
  theme,
  color,
}: {
  theme: DefaultTheme
  color: LinkProps['color']
}): CSSObject => {
  const hoverColor = getLinkTextColor({ color: color ?? 'brand', hovered: true, theme })
  const visitedColor = getLinkTextColor({ color: color ?? 'brand', visited: true, theme })
  const hoverIconColor = getLinkIconColor({ color: color ?? 'brand', hovered: true, theme })
  const visitedIconColor = getLinkIconColor({ color: color ?? 'brand', visited: true, theme })

  return {
    alignItems: 'center',
    columnGap: theme.designSystem.size.spacing.s,
    display: 'inline-flex',
    flexDirection: 'row',
    width: 'fit-content',
    ['&:hover span']: {
      color: hoverColor,
    },
    ['&:hover svg path']: {
      fill: hoverIconColor,
    },
    ['&:visited span']: {
      color: visitedColor,
    },
    ['&:visited svg path']: {
      fill: visitedIconColor,
    },
  }
}

const Container = webStyled(LinkAnchor)<{
  color: LinkProps['color']
}>(getContainerStyle)

const InlineContent = webStyled.span<{ size: LinkSize }>(({ theme, size }) => ({
  alignItems: 'center',
  columnGap: getLinkGap({ size, theme }),
  display: 'inline-flex',
  flexDirection: 'row',
}))

const Text = webStyled.span<{
  size: LinkSize
  color: LinkProps['color']
  textColor?: LinkProps['textColor']
}>(({ theme, size, color = 'brand', textColor }) => ({
  color: textColor ?? getLinkTextColor({ color, theme }),
  textDecoration: 'underline',
  ...theme.designSystem.typography[getLinkTypography(size)],
}))

const InlineTextContainer = webStyled(LinkAnchor)<{
  color: LinkProps['color']
  $textColor?: LinkProps['textColor']
  $size: LinkSize
}>(({ theme, color = 'brand', $textColor, $size }) => {
  const hoverColor = $textColor ?? getLinkTextColor({ color, hovered: true, theme })
  const visitedColor = $textColor ?? getLinkTextColor({ color, visited: true, theme })
  const hoverIconColor = getLinkIconColor({ color, hovered: true, theme })
  const visitedIconColor = getLinkIconColor({ color, visited: true, theme })

  return {
    backgroundColor: 'transparent',
    border: 'none',
    boxSizing: 'border-box',
    color: $textColor ?? getLinkTextColor({ color, theme }),
    cursor: 'pointer',
    display: 'inline',
    margin: 0,
    outline: 'none',
    padding: 0,
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    ...theme.designSystem.typography[getLinkTypography($size)],
    ['& svg']: {
      marginRight: theme.designSystem.size.spacing.xs,
      transform: 'translateY(-0.05em)',
      verticalAlign: 'middle',
    },
    ['&:hover']: {
      color: hoverColor,
    },
    ['&:hover svg path']: {
      fill: hoverIconColor,
    },
    ['&:visited']: {
      color: visitedColor,
    },
    ['&:visited svg path']: {
      fill: visitedIconColor,
    },
  }
})

const InlineTextLabel = webStyled.span({
  whiteSpace: 'normal',
})
